import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCardsByOrganization,
  submitCardQuiz,
} from "../store/reducers/quizzesSlice";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaSearchPlus,
  FaSearchMinus,
} from "react-icons/fa";
import { Box, Typography, Paper, useTheme, useMediaQuery } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import "bootstrap/dist/css/bootstrap.min.css";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import QuizAlertModal from "./components/QuizAlertModal";
import { setQuizAlertModal } from "../store/reducers/modalSlice";
import { toWords } from "number-to-words";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const getDefaultViewType = (card) => {
  if (card?.front_pdf) return "front";
  if (card?.back_pdf) return "back";
  return "front";
};

const normalizeTargetCountries = (value) => {
  if (!value) return [];

  let parsedValue = value;

  if (typeof value === "string") {
    try {
      parsedValue = JSON.parse(value);
    } catch {
      parsedValue = value.split(",");
    }
  }

  if (!Array.isArray(parsedValue)) {
    parsedValue = [parsedValue];
  }

  return Array.from(
    new Set(
      parsedValue
        .map((item) =>
          typeof item === "object"
            ? item?.target_country ||
              item?.country ||
              item?.label ||
              item?.value
            : item,
        )
        .map((item) => `${item || ""}`.trim())
        .filter(Boolean),
    ),
  );
};

const CardPdfViewer = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { quizAlertModal } = useSelector((state) => state.modal || {});
  const { cardQuiz, cards } = useSelector((state) => state.quizzes || {});
  const { organizationDetails } = useSelector((state) => state.organization);

  const [selectedCardForQuiz, setSelectedCardForQuiz] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [viewType, setViewType] = useState("front");
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(isMobile ? 0.92 : 1.2);
  const [fileBlob, setFileBlob] = useState(null);
  const [viewerWidth, setViewerWidth] = useState(0);
  const viewerRef = useRef(null);

  useEffect(() => {
    if (organizationDetails?.id) {
      dispatch(fetchCardsByOrganization(organizationDetails.id));
    }
  }, [organizationDetails?.id, dispatch]);

  const cardList = useMemo(
    () => (Array.isArray(cards?.data) ? cards.data : []),
    [cards],
  );

  const availableTargetCountries = useMemo(() => {
    const countriesFromStore = Array.isArray(cards?.targetCountries)
      ? cards.targetCountries
      : [];

    if (countriesFromStore.length) {
      return countriesFromStore;
    }

    return Array.from(
      new Set(
        cardList.flatMap(
          (card) =>
            card?.target_countries ||
            normalizeTargetCountries(card?.target_country),
        ),
      ),
    );
  }, [cards, cardList]);

  useEffect(() => {
    if (!availableTargetCountries.length) {
      setSelectedCountry("");
      return;
    }

    setSelectedCountry((prev) =>
      availableTargetCountries.includes(prev)
        ? prev
        : availableTargetCountries[0],
    );
  }, [availableTargetCountries]);

  const filteredCards = useMemo(() => {
    if (!selectedCountry) {
      return cardList;
    }

    return cardList.filter((card) => {
      const countries =
        card?.target_countries ||
        normalizeTargetCountries(card?.target_country);

      return countries.some(
        (country) => country.toLowerCase() === selectedCountry.toLowerCase(),
      );
    });
  }, [cardList, selectedCountry]);

  useEffect(() => {
    if (!filteredCards.length) {
      setSelectedCardId(null);
      setViewType("front");
      setZoom(isMobile ? 0.92 : 1.2);
      return;
    }

    const activeCard = filteredCards.find((card) => card.id === selectedCardId);

    if (!activeCard) {
      const firstCard = filteredCards[0];
      setSelectedCardId(firstCard.id);
      setViewType(getDefaultViewType(firstCard));
      setZoom(isMobile ? 0.92 : 1.2);
    }
  }, [filteredCards, selectedCardId, isMobile]);

  const selectedIndex = useMemo(
    () => filteredCards.findIndex((card) => card.id === selectedCardId),
    [filteredCards, selectedCardId],
  );

  const selectedCard = useMemo(
    () =>
      selectedIndex >= 0
        ? filteredCards[selectedIndex]
        : filteredCards[0] || null,
    [filteredCards, selectedIndex],
  );

  const hasFrontPdf = Boolean(selectedCard?.front_pdf);
  const hasBackPdf = Boolean(selectedCard?.back_pdf);
  const requiresBackReview = hasFrontPdf && hasBackPdf;
  const canMarkCardRead = selectedCard && (!requiresBackReview || viewType === "back");
  const rawUrl =
    viewType === "front" ? selectedCard?.front_pdf : selectedCard?.back_pdf;

  useEffect(() => {
    if (!selectedCard) return;

    const activeUrl =
      viewType === "front" ? selectedCard?.front_pdf : selectedCard?.back_pdf;

    if (!activeUrl) {
      setViewType(getDefaultViewType(selectedCard));
    }
  }, [selectedCard, viewType]);

  useEffect(() => {
    setNumPages(null);

    if (!rawUrl) {
      setFileBlob(null);
      return;
    }

    let objectUrl;

    const loadPdf = async () => {
      try {
        const res = await fetch(rawUrl, { cache: "no-store" });
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        setFileBlob(objectUrl);
      } catch (err) {
        console.error("PDF load error", err);
      }
    };

    loadPdf();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [rawUrl]);

  const selectCard = (index) => {
    const nextCard = filteredCards?.[index];

    if (!nextCard) return;

    setSelectedCardId(nextCard.id);
    setViewType(getDefaultViewType(nextCard));
    setZoom(isMobile ? 0.92 : 1.2);
  };

  useEffect(() => {
    const viewerElement = viewerRef.current;

    if (!viewerElement) return undefined;

    let animationFrameId = null;

    const updateViewerWidth = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        const nextWidth = viewerElement.clientWidth;
        setViewerWidth((currentWidth) =>
          currentWidth === nextWidth ? currentWidth : nextWidth,
        );
      });
    };

    updateViewerWidth();

    const resizeObserver = new ResizeObserver(updateViewerWidth);
    resizeObserver.observe(viewerElement);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      resizeObserver.disconnect();
    };
  }, [isMobile, fileBlob]);

  useEffect(() => {
    const blockViewerEvent = (event) => {
      if (!viewerRef.current?.contains(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    const blockShortcuts = (event) => {
      const isMetaPressed = event.ctrlKey || event.metaKey;
      const blockedKey = event.key?.toLowerCase();

      if (isMetaPressed && ["c", "s", "p"].includes(blockedKey)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("contextmenu", blockViewerEvent, true);
    document.addEventListener("copy", blockViewerEvent, true);
    document.addEventListener("cut", blockViewerEvent, true);
    document.addEventListener("dragstart", blockViewerEvent, true);
    document.addEventListener("selectstart", blockViewerEvent, true);
    window.addEventListener("keydown", blockShortcuts, true);

    return () => {
      document.removeEventListener("contextmenu", blockViewerEvent, true);
      document.removeEventListener("copy", blockViewerEvent, true);
      document.removeEventListener("cut", blockViewerEvent, true);
      document.removeEventListener("dragstart", blockViewerEvent, true);
      document.removeEventListener("selectstart", blockViewerEvent, true);
      window.removeEventListener("keydown", blockShortcuts, true);
    };
  }, []);

  const handleNextCard = () => {
    if (selectedIndex < filteredCards.length - 1) {
      selectCard(selectedIndex + 1);
    }
  };

  const handlePrevCard = () => {
    if (selectedIndex > 0) {
      selectCard(selectedIndex - 1);
    }
  };

  const handleDone = () => {
    if (!canMarkCardRead) {
      return;
    }

    setSelectedCardForQuiz(selectedCard);
    dispatch(setQuizAlertModal(true));
  };

  const handleYes = async () => {
    if (!selectedCardForQuiz) return;

    dispatch(setQuizAlertModal(false));

    const payload = {
      card_id: selectedCardForQuiz?.id,
      organization_id: organizationDetails?.id,
      want_card_quiz: true,
    };

    const result = await dispatch(submitCardQuiz(payload));

    if (submitCardQuiz.fulfilled.match(result)) {
      toast.success("Quiz request submitted successfully!");
      dispatch(setQuizAlertModal(false));
    }

    setSelectedCardForQuiz(null);
  };

  const handleNo = () => {
    setSelectedCardForQuiz(null);
    dispatch(setQuizAlertModal(false));
  };

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.2, isMobile ? 1.8 : 3));
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.2, isMobile ? 0.7 : 0.6));

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const number_of_quiz = toWords(
    organizationDetails?.cards_questions_per_quiz || 0,
  );

  return (
    <div className="min-h-[93vh] overflow-auto bg-white p-3 text-[#20232A] sm:p-4">
      <h1 className="mb-4 text-[24px] font-bold leading-tight text-black md:text-[30px]">
        {t("userMenuSidebar.label.View_AC")}
      </h1>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          height: isMobile ? "auto" : "calc(93vh - 72px)",
        }}
      >
      {/* Sidebar (Desktop only) */}
      {!isMobile && (
        <Box
          sx={{
            width: "28%",
            overflowY: "auto",
            borderRight: "1px solid #ddd",
            p: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Page List
          </Typography>

          {filteredCards.length ? (
            filteredCards.map((card, index) => (
              <Paper
                key={card.id}
                onClick={() => selectCard(index)}
                sx={{
                  p: 1.5,
                  mb: 1,
                  cursor: "pointer",
                  backgroundColor:
                    index === selectedIndex
                      ? "var(--org-bg-tint-1, #ffebee)"
                      : "#fff",
                  border:
                    index === selectedIndex
                      ? "1px solid #ef5350"
                      : "1px solid #eee",
                }}
              >
                <Typography variant="body2">
                  <strong>{card.ac_no}</strong> - {card.english_name}
                </Typography>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No action cards found for the selected target country.
            </Typography>
          )}
        </Box>
      )}

      {/* Main Content */}
      <Box
        sx={{
          width: isMobile ? "100%" : "72%",
          p: isMobile ? 1 : 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {availableTargetCountries.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <label
              htmlFor="view-ac-target-country"
              className="form-label mb-1 fw-semibold"
            >
              Target Country
            </label>
            <select
              id="view-ac-target-country"
              className="form-select"
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              {availableTargetCountries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </Box>
        )}

        {/* Mobile Dropdown */}
        {isMobile && (
          <Box sx={{ mb: 1 }}>
            <select
              className="form-select"
              value={selectedIndex >= 0 ? selectedIndex : ""}
              onChange={(e) => {
                const index = Number(e.target.value);
                selectCard(index);
              }}
              disabled={!filteredCards.length}
            >
              {!filteredCards.length && (
                <option value="">No cards available</option>
              )}
              {filteredCards.map((card, index) => (
                <option key={card.id} value={index}>
                  {card.ac_no} - {card.english_name}
                </option>
              ))}
            </select>
          </Box>
        )}

        {/* Header */}
        <Box
          sx={{
            mb: 1,
            p: 1,
            borderRadius: "6px",
            backgroundColor: "#f5f5f5",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {selectedCard
              ? `${selectedCard?.english_name} - ${
                  viewType === "front" ? "Front" : "Back"
                }`
              : "No action card selected"}
          </Typography>
        </Box>

        {/* Front / Back Toggle */}
        <Box className="mb-2 d-flex justify-content-end">
          {hasFrontPdf && (
            <button
              className={`btn btn-sm rounded-pill px-4 ${
                hasBackPdf ? "me-2" : ""
              } ${
                viewType === "front" ? "btn-primary" : "btn-outline-primary"
              }`}
              style={isMobile ? { flex: 1, minHeight: 42 } : undefined}
              onClick={() => setViewType("front")}
            >
              Front Side
            </button>
          )}

          {hasBackPdf && (
            <button
              className={`btn btn-sm rounded-pill px-4 ${
                viewType === "back" ? "btn-primary" : "btn-outline-primary"
              }`}
              style={isMobile ? { flex: 1, minHeight: 42 } : undefined}
              onClick={() => setViewType("back")}
            >
              Back Side
            </button>
          )}
        </Box>

        {/* PDF Viewer */}
        <Box
          ref={viewerRef}
          sx={{
            flex: 1,
            overflow: "auto",
            border: "1px solid #ccc",
            borderRadius: "8px",
            p: isMobile ? 1 : 2,
            backgroundColor: "#fafafa",
            position: "relative",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
            minHeight: isMobile ? "58vh" : undefined,
            "& canvas": {
              userSelect: "none",
              WebkitUserDrag: "none",
              maxWidth: "100%",
              height: "auto !important",
            },
            "& img": {
              pointerEvents: "none",
              userSelect: "none",
              WebkitUserDrag: "none",
            },
          }}
        >
          {fileBlob ? (
            <Document
              key={fileBlob}
              file={fileBlob}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <span className="spinner-border spinner-border-sm"></span>
              }
            >
              {Array.from(new Array(numPages), (_, i) => (
                <Box key={i} mb={2} display="flex" justifyContent="center">
                  <Page
                    pageNumber={i + 1}
                    scale={isMobile ? undefined : zoom}
                    width={
                      isMobile && viewerWidth
                        ? Math.max(220, Math.floor((viewerWidth - 16) * zoom))
                        : undefined
                    }
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Box>
              ))}
            </Document>
          ) : (
            <Typography align="center" mt={5}>
              {filteredCards.length
                ? "No PDF available"
                : "No action cards available for this target country"}
            </Typography>
          )}

          {/* Zoom Controls — sticky bottom-right overlay inside viewer */}
          <Box
            sx={{
              position: "sticky",
              bottom: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: isMobile ? "center" : "flex-end",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "row" : "column",
                gap: 1,
                pointerEvents: "auto",
              }}
            >
              <button
                className="btn btn-light btn-sm shadow-sm"
                onClick={zoomIn}
                title="Zoom In"
                style={{
                  width: 36,
                  height: 36,
                  padding: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaSearchPlus size={14} />
              </button>
              <button
                className="btn btn-light btn-sm shadow-sm"
                onClick={zoomOut}
                title="Zoom Out"
                style={{
                  width: 36,
                  height: 36,
                  padding: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaSearchMinus size={14} />
              </button>
            </Box>
          </Box>
        </Box>

        {/* Navigation Bar */}
        <Box
          sx={{
            mt: isMobile ? 2 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: isMobile ? "center" : "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {/* Left: Done button — always reserves space so layout stays stable */}
          <Box sx={{ minWidth: isMobile ? "100%" : 190, order: isMobile ? 2 : 0 }}>
            {canMarkCardRead && (
              <button
                className="btn btn-success btn-sm d-flex align-items-center justify-content-center gap-1"
                onClick={handleDone}
                style={isMobile ? { width: "100%", minHeight: 38 } : undefined}
              >
                <FaCheck size={14} />
                I've now read this card.
              </button>
            )}
          </Box>

          {/* Right: Prev < Card X of Y > Next */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              minWidth: 0,
              width: isMobile ? "100%" : "auto",
            }}
          >
            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
              onClick={handlePrevCard}
              disabled={selectedIndex <= 0}
              style={{
                width: 34,
                height: 34,
                padding: 0,
                borderRadius: "50%",
              }}
            >
              <FaArrowLeft size={13} />
            </button>

            <Typography
              variant="body2"
              sx={{
                minWidth: 90,
                textAlign: "center",
                fontWeight: 500,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Card <strong>{selectedIndex >= 0 ? selectedIndex + 1 : 1}</strong>
              {" of "}
              <strong>{filteredCards.length}</strong>
            </Typography>

            <button
              className="btn btn-primary btn-sm d-flex align-items-center justify-content-center"
              onClick={handleNextCard}
              disabled={
                selectedIndex < 0 || selectedIndex === filteredCards.length - 1
              }
              style={{
                width: 34,
                height: 34,
                padding: 0,
                borderRadius: "50%",
              }}
            >
              <FaArrowRight size={13} />
            </button>
          </Box>
        </Box>
      </Box>

      {quizAlertModal && (
        <QuizAlertModal
          description={`You have read an Action card. Do you want to increase your score by answering ${number_of_quiz} quick questions related to the cards?`}
          subText={`Questions will be sent after ${organizationDetails?.delay_cards_quiz} hours.`}
          open={quizAlertModal}
          loading={cardQuiz?.loading}
          onClose={handleNo}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
      </Box>
    </div>
  );
};

export default CardPdfViewer;
